import type { ReactElement } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ChatMessageSummary,
  PrivateChatMessageSummary,
  PrivateChatThreadSummary,
  PrivateQueueEntrySummary,
  ShowInteractionSummary,
} from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button, ShowChatPanel, ShowChatShell } from '@fetmode/ui';
import { formatUsername, isPrivateQueueAvailable, isPublicMainStage } from '@fetmode/utils';
import { Link } from 'react-router-dom';

import { useStudioModel } from '../../../features/studio/index.js';
import './StudioGlobalChat.css';

const CHAT_POLL_MS = 2_000;
const PRIVATE_POLL_MS = 2_500;
const QUEUE_POLL_MS = 2_500;
const DOCK_OPEN_KEY = 'fetmode.studio.chatDockOpen';

function readDockOpen(): boolean {
  try {
    return window.localStorage.getItem(DOCK_OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

function writeDockOpen(open: boolean): void {
  try {
    window.localStorage.setItem(DOCK_OPEN_KEY, open ? '1' : '0');
  } catch {
    /* ignore quota / private mode */
  }
}

type ChatMode = 'room' | 'private' | 'queue';

function countablePublicMessage(message: ChatMessageSummary): boolean {
  return message.kind === 'USER' || message.kind === 'TIP';
}

function queueItemKey(kind: 'queue' | 'interaction', id: string): string {
  return `${kind}:${id}`;
}

function toPanelMessages(messages: PrivateChatMessageSummary[]): ChatMessageSummary[] {
  return messages.map((message) => ({
    id: message.id,
    showId: message.modelId,
    userId: message.senderUserId,
    username: message.senderUsername,
    body:
      message.kind === 'IMAGE'
        ? message.body.trim().length > 0
          ? `📷 ${message.body}`
          : '📷 Photo'
        : message.body,
    kind: 'USER' as const,
    createdAt: message.createdAt,
  }));
}

function formatQueueDuration(entry: PrivateQueueEntrySummary): string {
  const durationLabel =
    entry.durationMinutes === null
      ? t('common.show.privateShowDurationUntimed')
      : t('common.show.privateShowDurationBlock').replace(
          '{{minutes}}',
          String(entry.durationMinutes),
        );
  const scheduleLabel = entry.scheduledAt
    ? new Date(entry.scheduledAt).toLocaleString()
    : t('common.show.privateShowTimingAsap');
  return `${durationLabel} · ${scheduleLabel}`;
}

type StudioQueueBoardItem =
  | {
      key: string;
      sortAt: number;
      kind: 'PRIVATE';
      entry: PrivateQueueEntrySummary;
    }
  | {
      key: string;
      sortAt: number;
      kind: 'TIP_MENU' | 'JUKEBOX';
      entry: ShowInteractionSummary;
    };

function buildChronologicalQueue(
  privateEntries: PrivateQueueEntrySummary[],
  interactions: ShowInteractionSummary[],
): StudioQueueBoardItem[] {
  const items: StudioQueueBoardItem[] = [
    ...privateEntries.map((entry) => ({
      key: queueItemKey('queue', entry.id),
      sortAt: new Date(entry.createdAt).getTime(),
      kind: 'PRIVATE' as const,
      entry,
    })),
    ...interactions.map((entry) => ({
      key: queueItemKey('interaction', entry.id),
      sortAt: new Date(entry.createdAt).getTime(),
      kind: entry.kind,
      entry,
    })),
  ];
  return items.sort((left, right) => left.sortAt - right.sortAt);
}

export interface StudioGlobalChatProps {
  /** Fixed corner dock (default) or page-embedded panel for the stream layout. */
  variant?: 'dock' | 'panel';
  /** Initial tab; defaults to room when live, otherwise private. */
  defaultMode?: ChatMode;
  focusPrivateUserId?: string | null;
  focusPrivateUsername?: string | null;
  onPrivateFocused?: () => void;
}

/**
 * Chat on studio pages: room chat (while live/break) + private inbox + private queue.
 */
export function StudioGlobalChat({
  variant = 'dock',
  defaultMode,
  focusPrivateUserId = null,
  focusPrivateUsername = null,
  onPrivateFocused,
}: StudioGlobalChatProps = {}): ReactElement | null {
  const { api, user } = useAuth();
  const { profile, liveShow, setLiveShow, requestsRevision, bumpRequestsRevision } = useStudioModel();
  const liveShowId = liveShow?.id ?? null;
  const liveShowStatus = liveShow?.status ?? null;
  const liveShowType = liveShow?.type ?? null;
  const queueAvailable = liveShow !== null && isPrivateQueueAvailable(liveShow);

  const [mode, setMode] = useState<ChatMode>(
    defaultMode ?? (liveShow ? 'room' : 'private'),
  );
  const [dockOpen, setDockOpen] = useState(readDockOpen);
  const dockOpenRef = useRef(dockOpen);
  dockOpenRef.current = dockOpen;
  const dockVisible = variant === 'panel' || dockOpen;
  const dockVisibleRef = useRef(dockVisible);
  dockVisibleRef.current = dockVisible;
  const [messages, setMessages] = useState<ChatMessageSummary[]>([]);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const sinceRef = useRef<string | undefined>(undefined);

  const [threads, setThreads] = useState<PrivateChatThreadSummary[]>([]);
  const [activeFanUserId, setActiveFanUserId] = useState<string | null>(null);
  const [privateMessages, setPrivateMessages] = useState<PrivateChatMessageSummary[]>([]);
  const [privateText, setPrivateText] = useState('');
  const [privateError, setPrivateError] = useState<string | null>(null);
  const [isPrivateSending, setIsPrivateSending] = useState(false);

  const [queueEntries, setQueueEntries] = useState<PrivateQueueEntrySummary[]>([]);
  const [interactions, setInteractions] = useState<ShowInteractionSummary[]>([]);
  const [queueError, setQueueError] = useState<string | null>(null);
  const [isQueueBusy, setIsQueueBusy] = useState(false);
  const [publicUnread, setPublicUnread] = useState(0);
  const [queueUnread, setQueueUnread] = useState(0);
  const [privateUnread, setPrivateUnread] = useState(0);
  const seenPublicIdsRef = useRef<Set<string>>(new Set());
  const seenQueueIdsRef = useRef<Set<string> | null>(null);
  const seenPrivateIdsRef = useRef<Set<string> | null>(null);
  const prevShowTypeRef = useRef<string | null>(null);
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const userIdRef = useRef(user?.id ?? null);
  userIdRef.current = user?.id ?? null;
  const focusPrivateUserIdRef = useRef(focusPrivateUserId);
  focusPrivateUserIdRef.current = focusPrivateUserId;

  const publicChatViewable = liveShow ? isPublicMainStage(liveShow.type) : false;

  const mergeMessages = useCallback((incoming: ChatMessageSummary[]) => {
    setMessages((current) => {
      if (incoming.length === 0) {
        return current;
      }
      const ids = new Set(current.map((message) => message.id));
      const merged = [...current, ...incoming.filter((message) => message.id && !ids.has(message.id))];
      return merged.slice(-100);
    });
  }, []);

  const loadChat = useCallback(
    async (since?: string) => {
      if (!liveShowId) {
        return;
      }

      const result = await api.listShowChat(liveShowId, { since, limit: 50 });
      if (since) {
        mergeMessages(result.messages);
      } else {
        setMessages(result.messages);
      }

      const lastMessage = result.messages.at(-1);
      if (lastMessage) {
        sinceRef.current = lastMessage.createdAt;
      } else if (!since) {
        sinceRef.current = undefined;
      }
    },
    [api, liveShowId, mergeMessages],
  );

  const loadThreads = useCallback(async () => {
    const result = await api.listPrivateChatThreads();
    setThreads(result.threads);

    const lastIds = result.threads.map((thread) => thread.lastMessage.id);
    const viewingPrivate =
      modeRef.current === 'private' && dockVisibleRef.current;
    if (seenPrivateIdsRef.current === null || viewingPrivate) {
      seenPrivateIdsRef.current = new Set(lastIds);
      setPrivateUnread(0);
    } else {
      const modelUserId = userIdRef.current;
      const unseen = result.threads.filter(
        (thread) =>
          !seenPrivateIdsRef.current?.has(thread.lastMessage.id) &&
          thread.lastMessage.senderUserId !== modelUserId,
      ).length;
      setPrivateUnread(unseen);
    }

    setActiveFanUserId((current) => {
      if (current && (result.threads.some((thread) => thread.fanUserId === current) || current === focusPrivateUserIdRef.current)) {
        return current;
      }
      return result.threads[0]?.fanUserId ?? null;
    });
  }, [api]);

  const loadPrivateThread = useCallback(
    async (fanUserId: string) => {
      if (!profile?.username) {
        return;
      }

      const result = await api.getPrivateChat(profile.username, { fanUserId });
      setPrivateMessages(result.messages);
    },
    [api, profile?.username],
  );

  const loadQueue = useCallback(async () => {
    if (!liveShowId || !queueAvailable) {
      setQueueEntries([]);
      setInteractions([]);
      seenQueueIdsRef.current = null;
      setQueueUnread(0);
      return;
    }

    // Load independently so a missing interactions route cannot blank the private queue.
    const [privateResult, interactionResult] = await Promise.allSettled([
      api.listPrivateQueue(liveShowId),
      api.listShowInteractions(liveShowId),
    ]);

    const nextPrivate =
      privateResult.status === 'fulfilled' ? privateResult.value.entries : [];
    const nextInteractions =
      interactionResult.status === 'fulfilled' ? interactionResult.value.interactions : [];

    if (privateResult.status === 'rejected' && interactionResult.status === 'rejected') {
      throw privateResult.reason instanceof Error
        ? privateResult.reason
        : new Error('Unable to load queue.');
    }

    if (privateResult.status === 'rejected' || interactionResult.status === 'rejected') {
      const failed =
        privateResult.status === 'rejected' ? privateResult : interactionResult;
      const reason = failed.status === 'rejected' ? failed.reason : null;
      setQueueError(
        reason instanceof ApiError
          ? reason.message
          : t('common.studio.live.queueLoadError'),
      );
    } else {
      setQueueError(null);
    }

    setQueueEntries(nextPrivate);
    setInteractions(nextInteractions);

    const nextIds = [
      ...nextPrivate.map((entry) => queueItemKey('queue', entry.id)),
      ...nextInteractions.map((entry) => queueItemKey('interaction', entry.id)),
    ];

    const viewingQueue =
      modeRef.current === 'queue' && (variant === 'panel' || dockOpenRef.current);
    if (seenQueueIdsRef.current === null || viewingQueue) {
      seenQueueIdsRef.current = new Set(nextIds);
      setQueueUnread(0);
      return;
    }

    const unseen = nextIds.filter((id) => !seenQueueIdsRef.current?.has(id)).length;
    setQueueUnread(unseen);
  }, [api, liveShowId, queueAvailable]);

  useEffect(() => {
    if (!liveShowId) {
      setMessages([]);
      sinceRef.current = undefined;
      seenPublicIdsRef.current = new Set();
      setPublicUnread(0);
      prevShowTypeRef.current = null;
      return;
    }

    void loadChat().catch(() => undefined);
    const intervalId = window.setInterval(() => {
      void loadChat(sinceRef.current).catch(() => undefined);
    }, CHAT_POLL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [liveShowId, loadChat]);

  useEffect(() => {
    if (!liveShow) {
      return;
    }

    const countable = messages.filter(countablePublicMessage);
    const showType = liveShow.type;
    const leftPublicFloor =
      prevShowTypeRef.current === 'MAIN_STAGE' && showType !== 'MAIN_STAGE';

    if (leftPublicFloor) {
      // Only badge messages that arrive after leaving Main Stage.
      seenPublicIdsRef.current = new Set(countable.map((message) => message.id));
      setPublicUnread(0);
    } else if (mode === 'room' && dockVisible) {
      seenPublicIdsRef.current = new Set(countable.map((message) => message.id));
      setPublicUnread(0);
    } else {
      const unseen = countable.filter((message) => !seenPublicIdsRef.current.has(message.id));
      setPublicUnread(unseen.length);
    }

    prevShowTypeRef.current = showType;
  }, [dockVisible, liveShow, messages, mode]);

  useEffect(() => {
    if (!liveShow && (mode === 'room' || mode === 'queue')) {
      setMode('private');
    }
  }, [liveShow, mode]);

  useEffect(() => {
    void loadThreads().catch(() => undefined);
    const intervalId = window.setInterval(() => {
      void loadThreads().catch(() => undefined);
    }, PRIVATE_POLL_MS);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [loadThreads]);

  useEffect(() => {
    if (!focusPrivateUserId) {
      return;
    }
    setMode('private');
    setActiveFanUserId(focusPrivateUserId);
    void loadPrivateThread(focusPrivateUserId).catch((err: unknown) => {
      setPrivateError(
        err instanceof ApiError ? err.message : t('common.studio.live.privateChatLoadError'),
      );
    });
    onPrivateFocused?.();
  }, [focusPrivateUserId, loadPrivateThread, onPrivateFocused]);

  useEffect(() => {
    if (!activeFanUserId || mode !== 'private') {
      return;
    }

    void loadPrivateThread(activeFanUserId).catch((err: unknown) => {
      setPrivateError(
        err instanceof ApiError ? err.message : t('common.studio.live.privateChatLoadError'),
      );
    });
  }, [activeFanUserId, loadPrivateThread, mode]);

  useEffect(() => {
    if (!liveShowId || !queueAvailable) {
      setQueueEntries([]);
      setInteractions([]);
      seenQueueIdsRef.current = null;
      setQueueUnread(0);
      return;
    }

    void loadQueue().catch(() => undefined);
    const intervalId = window.setInterval(() => {
      void loadQueue().catch(() => undefined);
    }, QUEUE_POLL_MS);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [liveShowId, liveShowStatus, liveShowType, loadQueue, queueAvailable]);

  useEffect(() => {
    if (!liveShowId || !queueAvailable || requestsRevision === 0) {
      return;
    }
    void loadQueue().catch(() => undefined);
  }, [liveShowId, loadQueue, queueAvailable, requestsRevision]);

  async function handleSendRoom(): Promise<void> {
    if (!liveShow || !user || !isPublicMainStage(liveShow.type)) {
      return;
    }

    const body = text.trim();
    if (body.length === 0) {
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      const result = await api.sendChatMessage(liveShow.id, { body });
      setText('');
      mergeMessages([result.message]);
      sinceRef.current = result.message.createdAt;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Unable to send message.');
    } finally {
      setIsSending(false);
    }
  }

  async function handleDelete(messageId: string): Promise<void> {
    if (!liveShow) {
      return;
    }

    try {
      await api.deleteChatMessage(liveShow.id, messageId);
      setMessages((current) => current.filter((message) => message.id !== messageId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Unable to remove message.');
    }
  }

  async function handleSendPrivate(): Promise<void> {
    const modelUsername = profile?.username;
    if (!activeFanUserId || !modelUsername) {
      return;
    }

    const body = privateText.trim();
    if (body.length === 0) {
      return;
    }

    setIsPrivateSending(true);
    setPrivateError(null);

    try {
      const result = await api.sendPrivateChatMessage(modelUsername, {
        kind: 'TEXT',
        body,
        fanUserId: activeFanUserId,
      });
      setPrivateText('');
      setPrivateMessages((current) => {
        if (current.some((item) => item.id === result.message.id)) {
          return current;
        }
        return [...current, result.message];
      });
      void loadThreads().catch(() => undefined);
    } catch (err) {
      setPrivateError(
        err instanceof ApiError ? err.message : t('common.studio.live.privateChatSendError'),
      );
    } finally {
      setIsPrivateSending(false);
    }
  }

  async function handleStartNextQueue(): Promise<void> {
    if (!liveShow) {
      return;
    }

    setIsQueueBusy(true);
    setQueueError(null);
    try {
      const result = await api.startNextPrivateQueue(liveShow.id);
      setLiveShow(result.show);
      await loadQueue();
    } catch (err) {
      setQueueError(
        err instanceof ApiError ? err.message : 'Could not start next private.',
      );
    } finally {
      setIsQueueBusy(false);
    }
  }

  async function handleSkipQueue(entryId: string): Promise<void> {
    if (!liveShow) {
      return;
    }

    setIsQueueBusy(true);
    setQueueError(null);
    try {
      await api.skipPrivateQueueEntry(liveShow.id, entryId);
      await loadQueue();
    } catch (err) {
      setQueueError(err instanceof ApiError ? err.message : 'Could not skip queue entry.');
    } finally {
      setIsQueueBusy(false);
    }
  }

  async function handleAcceptInteraction(interactionId: string): Promise<void> {
    if (!liveShow) {
      return;
    }

    setIsQueueBusy(true);
    setQueueError(null);
    try {
      await api.acceptShowInteraction(liveShow.id, interactionId);
      bumpRequestsRevision();
      await loadQueue();
    } catch (err) {
      setQueueError(err instanceof ApiError ? err.message : 'Could not accept request.');
    } finally {
      setIsQueueBusy(false);
    }
  }

  async function handleDeclineInteraction(interactionId: string): Promise<void> {
    if (!liveShow) {
      return;
    }

    setIsQueueBusy(true);
    setQueueError(null);
    try {
      await api.declineShowInteraction(liveShow.id, interactionId);
      await loadQueue();
    } catch (err) {
      setQueueError(err instanceof ApiError ? err.message : 'Could not decline request.');
    } finally {
      setIsQueueBusy(false);
    }
  }

  const queueBadgeCount = queueUnread;
  const publicBadgeCount = publicUnread;
  const chronologicalQueue = buildChronologicalQueue(queueEntries, interactions);
  const activeThread = threads.find((thread) => thread.fanUserId === activeFanUserId) ?? null;
  const privateStatusName = activeThread
    ? formatUsername(activeThread.fanUsername)
    : focusPrivateUsername
      ? formatUsername(focusPrivateUsername)
      : t('common.studio.live.privateChatInbox');
  const statusLabel =
    mode === 'private'
      ? privateStatusName
      : mode === 'queue'
        ? t('common.studio.live.queueTitle')
        : liveShow?.status === 'PAUSED'
          ? t('common.studio.live.onBreak')
          : liveShow?.status === 'AWAY'
            ? t('common.studio.live.awayStatus')
            : liveShow
              ? publicChatViewable
                ? t('common.studio.live.liveNow')
                : liveShow.access.typeLabel
              : t('common.studio.live.chatOffline');

  const tabs = [
    {
      id: 'room',
      label: t('common.show.chatTabPublic'),
      badge: publicBadgeCount > 0 ? publicBadgeCount : undefined,
    },
    {
      id: 'private',
      label: t('common.show.chatTabPrivate'),
      badge: privateUnread > 0 ? privateUnread : undefined,
    },
    ...(liveShow
      ? [
          {
            id: 'queue',
            label: t('common.studio.live.chatTabQueue'),
            badge: queueBadgeCount > 0 ? queueBadgeCount : undefined,
          },
        ]
      : []),
  ];

  function markQueueSeen(): void {
    const nextIds = [
      ...queueEntries.map((entry) => queueItemKey('queue', entry.id)),
      ...interactions.map((entry) => queueItemKey('interaction', entry.id)),
    ];
    seenQueueIdsRef.current = new Set(nextIds);
    setQueueUnread(0);
  }

  function markPrivateSeen(): void {
    seenPrivateIdsRef.current = new Set(threads.map((thread) => thread.lastMessage.id));
    setPrivateUnread(0);
  }

  function setDockOpenAndStore(open: boolean): void {
    setDockOpen(open);
    writeDockOpen(open);
    if (open && mode === 'queue') {
      markQueueSeen();
    }
    if (open && mode === 'private') {
      markPrivateSeen();
    }
  }

  if (variant === 'dock' && !user) {
    return null;
  }

  if (variant === 'dock' && !dockOpen) {
    const launcherBadge = publicBadgeCount + queueBadgeCount + privateUnread;
    const launcherAria =
      launcherBadge > 0
        ? t('common.studio.live.chatExpandAriaUnread').replace(
            '{{count}}',
            String(launcherBadge),
          )
        : t('common.studio.live.chatExpandAria');
    return (
      <button
        type="button"
        className="studio-global-chat__launcher"
        aria-expanded="false"
        aria-label={launcherAria}
        onClick={() => {
          setDockOpenAndStore(true);
        }}
      >
        <span>{t('common.studio.live.chatTitle')}</span>
        {launcherBadge > 0 ? (
          <span className="studio-global-chat__launcher-badge">{launcherBadge}</span>
        ) : null}
      </button>
    );
  }

  return (
    <ShowChatShell
      className={
        variant === 'panel'
          ? 'studio-global-chat studio-global-chat--panel'
          : 'studio-global-chat studio-global-chat--dock'
      }
      tablistLabel={t('common.show.chatTitle')}
      tabs={tabs}
      activeTabId={mode}
      onTabChange={(tabId) => {
        if (tabId === 'private' || tabId === 'queue' || tabId === 'room') {
          setMode(tabId);
          if (tabId === 'queue') {
            markQueueSeen();
          }
          if (tabId === 'private') {
            markPrivateSeen();
          }
        }
        setError(null);
        setPrivateError(null);
        setQueueError(null);
      }}
      status={statusLabel}
      headerAction={
        variant === 'dock' ? (
          <button
            type="button"
            className="studio-global-chat__collapse"
            aria-label={t('common.studio.live.chatCollapseAria')}
            onClick={() => {
              setDockOpenAndStore(false);
            }}
          >
            {t('common.studio.live.chatCollapse')}
          </button>
        ) : null
      }
    >
      {mode === 'room' ? (
        liveShow ? (
          publicChatViewable ? (
            <ShowChatPanel
              layout="room"
              messages={messages}
              messageText={text}
              onMessageTextChange={setText}
              onSend={() => {
                void handleSendRoom();
              }}
              isSending={isSending}
              sendError={error}
              canSend
              emptyPrompt={t('common.studio.live.chatEmpty')}
              inputPlaceholder={t('common.studio.live.chatPlaceholder')}
              sendLabel={t('common.show.chatSend')}
              sendingLabel={t('common.show.chatSending')}
              deleteLabel={t('common.studio.live.chatDelete')}
              onDeleteMessage={(messageId) => {
                void handleDelete(messageId);
              }}
            />
          ) : (
            <div className="show-chat-shell__empty">
              <p style={{ margin: 0 }}>{t('common.studio.live.publicChatPaused')}</p>
              <p className="studio-global-chat__paused-hint">
                {t('common.studio.live.publicChatPausedHint')}
              </p>
            </div>
          )
        ) : (
          <div className="show-chat-shell__empty">
            <p style={{ margin: 0 }}>{t('common.studio.live.chatOfflineHint')}</p>
            <Link to="/live" className="show-chat-shell__empty-link">
              {t('common.studio.nav.stream')}
            </Link>
          </div>
        )
      ) : mode === 'queue' ? (
        <div className="studio-global-chat__queue">
          {!liveShow || !queueAvailable ? (
            <p className="show-chat-shell__empty">{t('common.studio.live.queueOfflineHint')}</p>
          ) : (
            <>
              <div className="studio-global-chat__queue-head">
                <h3>{t('common.studio.live.queueTitle')}</h3>
                <Button
                  label={t('common.studio.live.queueStartNext')}
                  disabled={isQueueBusy || queueEntries.length === 0}
                  onClick={() => {
                    void handleStartNextQueue();
                  }}
                />
              </div>
              {queueError ? <p className="studio-global-chat__queue-error">{queueError}</p> : null}
              {chronologicalQueue.length === 0 ? (
                <p className="show-chat-shell__empty">{t('common.studio.live.queueEmpty')}</p>
              ) : (
                <ul className="studio-global-chat__queue-list">
                  {chronologicalQueue.map((item, index) => {
                    if (item.kind === 'PRIVATE') {
                      const entry = item.entry;
                      return (
                        <li key={item.key} className="studio-global-chat__queue-item">
                          <div>
                            <p className="studio-global-chat__queue-meta">
                              {t('common.studio.live.queueKindPrivate')} · #{String(index + 1)}
                            </p>
                            <p className="studio-global-chat__queue-title">
                              {t('common.studio.live.queuePosition')
                                .replace('{{position}}', String(entry.position))
                                .replace('{{username}}', formatUsername(entry.username))
                                .replace('{{duration}}', formatQueueDuration(entry))
                                .replace('{{type}}', entry.type)}
                            </p>
                          </div>
                          <Button
                            label={t('common.studio.live.queueSkip')}
                            variant="secondary"
                            disabled={isQueueBusy}
                            onClick={() => {
                              void handleSkipQueue(entry.id);
                            }}
                          />
                        </li>
                      );
                    }

                    const entry = item.entry;
                    return (
                      <li key={item.key} className="studio-global-chat__queue-item">
                        <div>
                          <p className="studio-global-chat__queue-meta">
                            {entry.kind === 'JUKEBOX'
                              ? t('common.studio.live.queueKindSong')
                              : t('common.studio.live.queueKindTipMenu')}{' '}
                            · #{String(index + 1)}
                          </p>
                          <p className="studio-global-chat__queue-title">
                            {t('common.studio.live.queueRequestItem')
                              .replace(
                                '{{kind}}',
                                entry.kind === 'JUKEBOX' ? 'Song' : 'Tip menu',
                              )
                              .replace('{{username}}', formatUsername(entry.username))
                              .replace('{{label}}', entry.label)
                              .replace('{{coins}}', String(entry.depositCoins))}
                          </p>
                          <p className="studio-global-chat__queue-meta">
                            {t('common.studio.live.queueRequestExpires').replace(
                              '{{time}}',
                              new Date(entry.expiresAt).toLocaleTimeString(),
                            )}
                          </p>
                        </div>
                        <div className="studio-global-chat__queue-actions">
                          <Button
                            label={
                              entry.kind === 'JUKEBOX'
                                ? t('common.studio.live.queuePlaySong')
                                : t('common.studio.live.queueAccept')
                            }
                            disabled={isQueueBusy}
                            onClick={() => {
                              void handleAcceptInteraction(entry.id);
                            }}
                          />
                          <Button
                            label={t('common.studio.live.queueDecline')}
                            variant="secondary"
                            disabled={isQueueBusy}
                            onClick={() => {
                              void handleDeclineInteraction(entry.id);
                            }}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="show-chat-shell__private">
          {threads.length === 0 && !activeFanUserId ? (
            <p className="show-chat-shell__empty">{t('common.studio.live.privateChatEmptyInbox')}</p>
          ) : (
            <>
              <div className="show-chat-shell__threads" role="list">
                {threads.map((thread) => (
                  <button
                    key={thread.fanUserId}
                    type="button"
                    role="listitem"
                    className={
                      thread.fanUserId === activeFanUserId
                        ? 'show-chat-shell__thread show-chat-shell__thread--active'
                        : 'show-chat-shell__thread'
                    }
                    onClick={() => {
                      setActiveFanUserId(thread.fanUserId);
                      setPrivateError(null);
                    }}
                  >
                    <span className="show-chat-shell__thread-name">
                      {formatUsername(thread.fanUsername)}
                    </span>
                    <span className="show-chat-shell__thread-preview">{thread.lastMessage.body}</span>
                  </button>
                ))}
              </div>
              <ShowChatPanel
                layout="room"
                messages={toPanelMessages(privateMessages)}
                messageText={privateText}
                onMessageTextChange={setPrivateText}
                onSend={() => {
                  void handleSendPrivate();
                }}
                isSending={isPrivateSending}
                sendError={privateError}
                canSend={Boolean(activeFanUserId)}
                emptyPrompt={t('common.studio.live.privateChatEmpty')}
                inputPlaceholder={t('common.studio.live.privateChatPlaceholder')}
                sendLabel={t('common.show.chatSend')}
                sendingLabel={t('common.show.chatSending')}
                renderMessage={(message) => (
                  <>
                    <strong>{formatUsername(message.username)}</strong> <span>{message.body}</span>
                  </>
                )}
              />
            </>
          )}
        </div>
      )}
    </ShowChatShell>
  );
}
